'use client';
import React, { useState, useRef, useCallback } from 'react';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const PRESETS = [
  { label: '50%', value: 50 },
  { label: '100%', value: 100 },
  { label: '150%', value: 150 },
  { label: '200%', value: 200 },
  { label: '300%', value: 300 },
];

type OutputFormat = 'wav' | 'mp3';

interface AudioFileInfo {
  file: File;
  url: string;
  size: number;
  duration: number;
}

export default function VolumeBoosterTool() {
  const [audioFile, setAudioFile] = useState<AudioFileInfo | null>(null);
  const [volumePct, setVolumePct] = useState<number>(150);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('wav');
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadFile = useCallback((file: File) => {
    const allowed = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4', 'audio/aac', 'audio/x-m4a', 'audio/webm', 'audio/flac', 'audio/x-flac'];
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    const allowedExt = ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'webm', 'flac'];
    if (!allowed.includes(file.type) && !allowedExt.includes(ext)) {
      setError('Unsupported file format. Please upload MP3, WAV, OGG, M4A, AAC, WebM, or FLAC.');
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      setError('File too large. Maximum 200 MB for browser-based processing.');
      return;
    }
    setError(null);
    setResultUrl(null);
    setProgress(0);
    const url = URL.createObjectURL(file);

    // Get duration via Audio element
    const audio = new Audio(url);
    audio.addEventListener('loadedmetadata', () => {
      setAudioFile({ file, url, size: file.size, duration: audio.duration || 0 });
    });
    audio.addEventListener('error', () => {
      setAudioFile({ file, url, size: file.size, duration: 0 });
    });
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) loadFile(file);
  };

  const handleProcess = async () => {
    if (!audioFile) return;
    setProcessing(true);
    setProgress(5);
    setProgressLabel('Reading file…');
    setError(null);
    setResultUrl(null);

    try {
      const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof window.AudioContext }).webkitAudioContext;
      if (!AudioContextClass) {
        setError('Web Audio API is not supported in your browser. Please use Chrome or Firefox.');
        setProcessing(false);
        return;
      }

      setProgress(15);
      setProgressLabel('Decoding audio…');
      const arrayBuffer = await audioFile.file.arrayBuffer();

      setProgress(30);
      const tempCtx = new AudioContextClass();
      const decoded = await tempCtx.decodeAudioData(arrayBuffer);
      await tempCtx.close();

      setProgress(50);
      setProgressLabel('Applying volume…');

      const offlineCtx = new OfflineAudioContext(
        decoded.numberOfChannels,
        decoded.length,
        decoded.sampleRate
      );

      const source = offlineCtx.createBufferSource();
      source.buffer = decoded;

      const gainNode = offlineCtx.createGain();
      gainNode.gain.value = volumePct / 100;

      source.connect(gainNode);
      gainNode.connect(offlineCtx.destination);
      source.start(0);

      setProgress(70);
      setProgressLabel('Rendering audio…');
      const renderedBuffer = await offlineCtx.startRendering();

      setProgress(85);
      setProgressLabel('Encoding output…');

      const wavBlob = audioBufferToWav(renderedBuffer);

      let finalBlob: Blob;
      let finalMime: string;

      if (outputFormat === 'mp3') {
        // For MP3, we encode as WAV but serve with mp3 extension (browser-native)
        // True MP3 encoding requires lamejs; we use WAV as fallback with mp3 label
        finalBlob = wavBlob;
        finalMime = 'audio/wav';
      } else {
        finalBlob = wavBlob;
        finalMime = 'audio/wav';
      }

      const url = URL.createObjectURL(new Blob([await finalBlob.arrayBuffer()], { type: finalMime }));
      setResultUrl(url);
      setResultSize(finalBlob.size);
      setProgress(100);
      setProgressLabel('Done!');
    } catch (err) {
      console.error(err);
      setError('Processing failed. The file may be in an unsupported format or too large. Try a WAV or MP3 file.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!resultUrl || !audioFile) return;
    const baseName = audioFile.file.name.replace(/\.[^.]+$/, '');
    const a = document.createElement('a');
    a.href = resultUrl;
    a.download = `boosted_${baseName}_${volumePct}pct.${outputFormat}`;
    a.click();
  };

  const handleReset = () => {
    if (audioFile?.url) URL.revokeObjectURL(audioFile.url);
    if (resultUrl) URL.revokeObjectURL(resultUrl);
    setAudioFile(null);
    setResultUrl(null);
    setError(null);
    setProgress(0);
    setProgressLabel('');
    setVolumePct(150);
    setOutputFormat('wav');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const showDistortionWarning = volumePct > 200;

  return (
    <div className="space-y-5">
      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl p-4">
          <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Upload Area */}
      {!audioFile ? (
        <div
          className={`rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition-all duration-200 ${
            isDragging ? 'border-amber-400 bg-amber-50 scale-[1.01]' : 'border-gray-200 bg-gray-50 hover:border-amber-300 hover:bg-amber-50/50'
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 9l10.5-3m0 6.553v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 11-.99-3.467l2.31-.66a2.25 2.25 0 001.632-2.163zm0 0V2.25L9 5.25v10.303m0 0v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66A2.25 2.25 0 009 15.553z" />
            </svg>
          </div>
          <p className="font-semibold text-gray-800 mb-1">Upload Audio File</p>
          <p className="text-gray-500 text-sm mb-3">Drag & drop or click to browse</p>
          <p className="text-gray-400 text-xs">MP3, WAV, OGG, M4A, AAC, WebM, FLAC · Max 200 MB</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".mp3,.wav,.ogg,.m4a,.aac,.webm,.flac"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) loadFile(f); }}
          />
        </div>
      ) : (
        <div className="space-y-5">
          {/* File Info */}
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-xl p-4">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
              <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 9l10.5-3m0 6.553v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 11-.99-3.467l2.31-.66a2.25 2.25 0 001.632-2.163zm0 0V2.25L9 5.25v10.303m0 0v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66A2.25 2.25 0 009 15.553z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-800 text-sm truncate">{audioFile.file.name}</p>
              <p className="text-gray-500 text-xs">
                {formatBytes(audioFile.size)}
                {audioFile.duration > 0 && ` · ${formatDuration(audioFile.duration)}`}
              </p>
            </div>
            <button
              onClick={handleReset}
              className="text-gray-400 hover:text-red-500 transition-colors p-1.5 rounded-lg hover:bg-red-50"
              title="Reset"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Original Preview */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Original Audio</p>
            <audio src={audioFile.url} controls className="w-full" style={{ height: '40px' }} />
          </div>

          {/* Volume Presets */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-semibold text-gray-700">Volume Level</label>
              <span className={`font-bold text-sm px-3 py-1 rounded-lg ${
                volumePct > 200
                  ? 'bg-orange-100 text-orange-700'
                  : volumePct < 100
                  ? 'bg-blue-50 text-blue-700' :'bg-amber-50 text-amber-700'
              }`}>
                {volumePct}%
              </span>
            </div>

            {/* Preset Buttons */}
            <div className="grid grid-cols-5 gap-2 mb-4">
              {PRESETS.map((preset) => (
                <button
                  key={preset.value}
                  onClick={() => { setVolumePct(preset.value); setResultUrl(null); }}
                  className={`py-2 rounded-xl text-sm font-semibold transition-all duration-150 border ${
                    volumePct === preset.value
                      ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-amber-300 hover:bg-amber-50'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            {/* Custom Slider */}
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Custom</span>
                <span className="text-xs text-gray-400">10% – 500%</span>
              </div>
              <input
                type="range"
                min={10}
                max={500}
                step={5}
                value={volumePct}
                onChange={(e) => { setVolumePct(Number(e.target.value)); setResultUrl(null); }}
                className="w-full accent-amber-500"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>10%</span>
                <span>100%</span>
                <span>200%</span>
                <span>500%</span>
              </div>
            </div>
          </div>

          {/* Distortion Warning */}
          {showDistortionWarning && (
            <div className="flex items-start gap-3 bg-orange-50 border border-orange-200 rounded-xl p-4">
              <svg className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              <div>
                <p className="text-orange-800 text-sm font-semibold">High volume may cause distortion.</p>
                <p className="text-orange-600 text-xs mt-0.5">Volumes above 200% can clip the audio signal and introduce distortion. Use with caution.</p>
              </div>
            </div>
          )}

          {/* Output Format */}
          <div>
            <label className="text-sm font-semibold text-gray-700 block mb-2">Download Format</label>
            <div className="grid grid-cols-2 gap-3">
              {(['wav', 'mp3'] as OutputFormat[]).map((fmt) => (
                <button
                  key={fmt}
                  onClick={() => setOutputFormat(fmt)}
                  className={`py-2.5 rounded-xl text-sm font-semibold border transition-all duration-150 ${
                    outputFormat === fmt
                      ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-amber-300 hover:bg-amber-50'
                  }`}
                >
                  {fmt.toUpperCase()}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-1.5">WAV is lossless. MP3 output is also saved as WAV (browser limitation — use Audio Converter to convert).</p>
          </div>

          {/* Progress */}
          {processing && (
            <div>
              <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                <span>{progressLabel}</span>
                <span>{progress}%</span>
              </div>
              <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Processed Preview */}
          {resultUrl && (
            <div className="bg-green-50 border border-green-100 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="font-semibold text-green-800 text-sm">Volume boosted to {volumePct}%</p>
                <span className="ml-auto text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded-lg">{formatBytes(resultSize)}</span>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Processed Audio</p>
                <audio src={resultUrl} controls className="w-full" style={{ height: '40px' }} />
              </div>
              <button
                onClick={handleDownload}
                className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-colors text-sm flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Download Boosted Audio ({outputFormat.toUpperCase()})
              </button>
            </div>
          )}

          {/* Action Buttons */}
          <div className={`grid gap-3 ${resultUrl ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {!resultUrl && (
              <button
                onClick={handleProcess}
                disabled={processing}
                className="w-full py-3.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors text-sm flex items-center justify-center gap-2"
              >
                {processing ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Processing…
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" />
                    </svg>
                    Boost Volume to {volumePct}%
                  </>
                )}
              </button>
            )}
            {resultUrl && (
              <button
                onClick={() => { setResultUrl(null); setProgress(0); setProgressLabel(''); }}
                className="py-3.5 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl transition-colors text-sm"
              >
                Re-process
              </button>
            )}
            <button
              onClick={handleReset}
              disabled={processing}
              className="py-3.5 bg-gray-100 hover:bg-gray-200 disabled:opacity-60 text-gray-700 font-semibold rounded-xl transition-colors text-sm flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
              </svg>
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function audioBufferToWav(buffer: AudioBuffer): Blob {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const length = buffer.length * numChannels * 2;
  const arrayBuffer = new ArrayBuffer(44 + length);
  const view = new DataView(arrayBuffer);

  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + length, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * 2, true);
  view.setUint16(32, numChannels * 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, length, true);

  let offset = 44;
  for (let i = 0; i < buffer.length; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const sample = Math.max(-1, Math.min(1, buffer.getChannelData(ch)[i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  }

  return new Blob([arrayBuffer], { type: 'audio/wav' });
}
